import { Fragment, useState, useContext } from "react";
import { Transition, Dialog } from "@headlessui/react";
import { StateContext } from "../../services/context";
import { classNames } from "../../utils/utils";
import {
  CancelIcon,
  InformationIcon,
  SuccessIcon,
  DeleteIcon,
} from "../../utils/iconFactory";
import { PrimaryButton, SecondaryRedButton } from "../Elements/CustomButtons";
import { CancelSubscriptionModal } from "./CancelSubscriptionModal";
import { useTheme } from "../../utils/themeUtils";
import { planData } from "../../utils/state";
import { PlanType } from "../../utils/utils";
import { api } from "../../services/POST";
import { PulseLoader } from "react-spinners";

type PlanDurationType = "havi" | "éves";

interface SubscriptionModalProps {
  isVisible: boolean;
  onClose: () => void;
}

//TODO:
//! ha eves elofizetese van akkor a havira is a lemondas gomb latszik

export function SubscriptionModal({
  isVisible,
  onClose,
}: SubscriptionModalProps) {
  const { themeString } = useTheme();
  const { user } = useContext(StateContext);
  const [planDurationSelect, setPlanDurationSelect] =
    useState<PlanDurationType>("havi");
  const [isSubButtonDisable, setIsSubButtonDisable] = useState(false);

  return (
    <Transition appear show={isVisible || isSubButtonDisable} as={Fragment}>
      <Dialog
        as="div"
        className={classNames("relative z-40", themeString)}
        onClose={onClose}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/25" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <Dialog.Panel className="relative h-[819px] w-[768px] rounded-[22px] bg-backgroundWhite pt-8 pb-6 text-left align-middle transform transition-all overflow-hidden modal-shadow">
                <Dialog.Title
                  as="h3"
                  className="text-textMain text-style-h3 border-b border-gray10 pb-4 mx-8 overflow-hidden"
                >
                  Előfizetés megváltoztatása
                </Dialog.Title>
                {isSubButtonDisable ? (
                  <>
                    <div
                      id="content"
                      className="flex flex-col gap-4 px-8 pt-5 items-center"
                    >
                      <div
                        id="padding-wrapper"
                        className="flex px-[52px] w-full justify-center"
                      >
                        <PulseLoader />
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <button
                      className="absolute top-9 right-6"
                      onClick={onClose}
                    >
                      <CancelIcon className="h-6 w-6 text-gray100 fill-current" />
                    </button>

                    <div
                      id="content"
                      className="flex flex-col gap-4 px-8 pt-5 items-center"
                    >
                      <div id="padding-wrapper" className="px-[52px] w-full">
                        <div
                          id="selector-buttons"
                          className="flex flex-row w-full justify-evenly bg-backgroundLightGray rounded-[12px]"
                        >
                          <button
                            className={classNames(
                              planDurationSelect === "havi"
                                ? "bg-textSecondary text-backgroundWhite text-style-body2-bold"
                                : "hover:bg-gray10 text-style-body2",
                              "w-full pt-[13px] pb-[11px] rounded-[12px]"
                            )}
                            onClick={() => setPlanDurationSelect("havi")}
                          >
                            Havi
                          </button>
                          <button
                            className={classNames(
                              planDurationSelect === "éves"
                                ? "bg-textSecondary text-backgroundWhite text-style-body2-bold"
                                : "hover:bg-gray10 text-style-body2",
                              "w-full pt-[13px] pb-[11px] rounded-[12px]"
                            )}
                            onClick={() => setPlanDurationSelect("éves")}
                          >
                            Éves
                          </button>
                        </div>
                      </div>

                      <div
                        id="tax-warn"
                        className="flex gap-2 items-center text-gray150"
                      >
                        <InformationIcon className="mb-px h-6 w-6 fill-current" />
                        <p className="text-style-small">
                          Az alábbi árak nettó árak, az áfát nem tartalmazzák.
                        </p>
                      </div>

                      <div id="plan-cards" className="flex gap-4">
                        {planDurationSelect === "havi" ? (
                          <>
                            <PlanCard
                              plan={planData.basic}
                              planDuration={planDurationSelect}
                              userPlan={user.plan}
                              isSubButtonDisable={isSubButtonDisable}
                              setIsSubButtonDisable={setIsSubButtonDisable}
                            />
                            <PlanCard
                              plan={planData.premium}
                              planDuration={planDurationSelect}
                              userPlan={user.plan}
                              isSubButtonDisable={isSubButtonDisable}
                              setIsSubButtonDisable={setIsSubButtonDisable}
                            />
                          </>
                        ) : (
                          <>
                            <PlanCard
                              plan={planData.basic_year}
                              planDuration={planDurationSelect}
                              userPlan={user.plan}
                              isSubButtonDisable={isSubButtonDisable}
                              setIsSubButtonDisable={setIsSubButtonDisable}
                            />
                            <PlanCard
                              plan={planData.premium_year}
                              planDuration={planDurationSelect}
                              userPlan={user.plan}
                              isSubButtonDisable={isSubButtonDisable}
                              setIsSubButtonDisable={setIsSubButtonDisable}
                            />
                          </>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition>
  );
}

interface PlanCardProps {
  plan: PlanType;
  planDuration: PlanDurationType;
  userPlan: string;
  isSubButtonDisable: any;
  setIsSubButtonDisable: (e: any) => void;
}

function PlanCard({
  plan,
  planDuration,
  userPlan,
  isSubButtonDisable,
  setIsSubButtonDisable,
}: PlanCardProps) {
  const [isOpenCancelSubscription, setIsOpenCancelSubscription] =
    useState(false);
  async function payment(planID: any) {
    if (isSubButtonDisable === false) {
      setIsSubButtonDisable(true);
      const data = await api("/payment", { planID: planID });
      window.parent.location.href = data.url;
    }
  }

  return (
    <>
      <CancelSubscriptionModal
        isVisible={isOpenCancelSubscription}
        onClose={() => setIsOpenCancelSubscription(false)}
      />
      {/* <div className="relative h-[584px] w-[344px] rounded-[16px] bg-backgroundDarkGray">
        {planDuration === "havi" && (
          <div className="flex items-center justify-center bg-[#16a34a] text-white rounded-t-[16px] py-2">
            {"Első hónap -90%"}
          </div>
        )}
        <div
          className={classNames(
            "flex flex-col flex-1 justify-between h-full px-4 pt-[19px] pb-4",
            planDuration === "havi" && "pb-[56px]"
          )}
        >
          <div id="content" className="flex flex-col gap-4">
            <h3
              id="name"
              className="text-textMain text-style-h3 border-b border-gray10 pb-2 overflow-hidden capitalize"
            >
              {plan.id.endsWith("_year")
                ? plan.id.slice(0, -"_year".length)
                : plan.id}
            </h3>
            <p
              id="cost-monthly"
              className="text-primaryActionBtnHover text-style-h3"
            >
              {plan.price} {planDuration === "havi" ? "Ft / hó" : "Ft / év"}
            </p>
            {plan.features.map((feature, index) => (
              <div key={index} id="feature" className="flex gap-2">
                <SuccessIcon className="h-6 w-6 text-primaryAction fill-current" />
                <p className="text-textMain text-style-body2">
                  {feature.label}
                </p>
              </div>
            ))}
          </div>
          {userPlan === plan.id ? (
            <SecondaryRedButton
              buttonInfo={{
                label: "Előfizetés lemondása",
                buttonIcon: DeleteIcon,
              }}
              onClick={() => {
                setIsOpenCancelSubscription(true);
              }}
            />
          ) : (
            <PrimaryButton
              buttonInfo={{ label: "Előfizetek" }}
              onClick={() => {
                payment(plan.planId);
              }}
            />
          )}
        </div>
      </div> */}
      <div className="relative h-[584px] w-[344px] rounded-[16px] bg-backgroundDarkGray">
        <div
          className={classNames(
            "flex flex-col flex-1 justify-between h-full px-4 pt-[19px] pb-4"
          )}
        >
          <div id="content" className="flex flex-col gap-4">
            <h3
              id="name"
              className="text-textMain text-style-h3 border-b border-gray10 pb-2 overflow-hidden capitalize"
            >
              {plan.id.endsWith("_year")
                ? plan.id.slice(0, -"_year".length)
                : plan.id}
            </h3>
            <p
              id="cost-monthly"
              className="text-primaryActionBtnHover text-style-h3"
            >
              {plan.price} {planDuration === "havi" ? "Ft / hó" : "Ft / év"}
            </p>
            {plan.features.map((feature, index) => (
              <div key={index} id="feature" className="flex gap-2">
                <SuccessIcon className="h-6 w-6 text-primaryAction fill-current" />
                <p className="text-textMain text-style-body2">
                  {feature.label}
                </p>
              </div>
            ))}
          </div>
          {userPlan === plan.id ? (
            <SecondaryRedButton
              buttonInfo={{
                label: "Előfizetés lemondása",
                buttonIcon: DeleteIcon,
              }}
              onClick={() => {
                setIsOpenCancelSubscription(true);
              }}
            />
          ) : (
            <PrimaryButton
              buttonInfo={{ label: "Előfizetek" }}
              onClick={() => {
                payment(plan.planId);
              }}
            />
          )}
        </div>
      </div>
    </>
  );
}
